import React, { useCallback, useEffect } from 'react';
import {
  ChevronLeft, ChevronRight, Check, Download, RefreshCw, Trash2, X,
} from 'lucide-react';
import { useSlotScreenGeneratorStore } from '@store/slotScreenGeneratorStore';
import type { ScreenComponentDef } from '@core/types/slot-screen-component.types';

interface GeneratorFullViewProps {
  component: ScreenComponentDef;
}

export function GeneratorFullView({ component }: GeneratorFullViewProps) {
  const fullViewIndex = useSlotScreenGeneratorStore(s => s.generatorFullViewIndex);
  const activeScreenId = useSlotScreenGeneratorStore(s => s.activeScreenId);
  const closeGeneratorFullView = useSlotScreenGeneratorStore(s => s.closeGeneratorFullView);
  const navigateGeneratorFullView = useSlotScreenGeneratorStore(s => s.navigateGeneratorFullView);
  const selectComponentResult = useSlotScreenGeneratorStore(s => s.selectComponentResult);
  const deleteComponentResult = useSlotScreenGeneratorStore(s => s.deleteComponentResult);

  const goPrev = useCallback(() => navigateGeneratorFullView('prev'), [navigateGeneratorFullView]);
  const goNext = useCallback(() => navigateGeneratorFullView('next'), [navigateGeneratorFullView]);

  // Keyboard handler
  useEffect(() => {
    if (fullViewIndex === null) return;

    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeGeneratorFullView();
      else if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [fullViewIndex, closeGeneratorFullView, goPrev, goNext]);

  if (fullViewIndex === null) return null;

  const readyResults = component.generatedResults.filter(r => r.status === 'ready');
  const result = readyResults[fullViewIndex];
  if (!result) return null;

  const total = readyResults.length;
  const isSelected = result.id === component.selectedResultId;

  const handleSelect = () => {
    selectComponentResult(activeScreenId, component.id, result.id);
  };

  const handleSave = () => {
    const url = result.type === 'video' && result.videoUrl ? result.videoUrl : result.imageUrl;
    if (!url) return;
    const a = document.createElement('a');
    a.href = url;
    a.download = `component-${component.id}-${fullViewIndex + 1}.${result.type === 'video' ? 'mp4' : 'png'}`;
    a.click();
  };

  const handleRegenerate = () => {
    console.log('[Screen Builder] Regenerate:', component.id, result.id);
  };

  const handleDelete = () => {
    closeGeneratorFullView();
    setTimeout(() => {
      deleteComponentResult(activeScreenId, component.id, result.id);
    }, 0);
  };

  return (
    <div className="absolute inset-0 z-50 bg-black/90 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-zinc-800 flex-shrink-0">
        {/* Left: back to gallery */}
        <button
          onClick={closeGeneratorFullView}
          className="flex items-center gap-1 text-sm text-zinc-400 hover:text-zinc-200 transition-colors"
        >
          <ChevronLeft size={16} />
          Gallery
        </button>

        {/* Center: counter */}
        <div className="flex items-center gap-3">
          {total > 1 && (
            <>
              <button
                onClick={goPrev}
                className="p-1 text-zinc-400 hover:text-white rounded hover:bg-zinc-700 transition-colors"
                title="Previous (Left Arrow)"
              >
                <ChevronLeft size={18} />
              </button>
              <span className="text-xs text-zinc-400 tabular-nums">
                {fullViewIndex + 1} / {total}
              </span>
              <button
                onClick={goNext}
                className="p-1 text-zinc-400 hover:text-white rounded hover:bg-zinc-700 transition-colors"
                title="Next (Right Arrow)"
              >
                <ChevronRight size={18} />
              </button>
            </>
          )}
        </div>

        {/* Right: close */}
        <button
          onClick={closeGeneratorFullView}
          className="p-1.5 text-zinc-400 hover:text-white rounded hover:bg-zinc-700 transition-colors"
          title="Close (Escape)"
        >
          <X size={18} />
        </button>
      </div>

      {/* Image area */}
      <div className="flex-1 flex items-center justify-center overflow-hidden p-4">
        {result.type === 'video' && result.videoUrl ? (
          <video
            src={result.videoUrl}
            controls
            className="max-w-full max-h-full object-contain rounded-lg"
          />
        ) : result.imageUrl ? (
          <img
            src={result.imageUrl}
            alt={`Result ${fullViewIndex + 1}`}
            className="max-w-full max-h-full object-contain rounded-lg"
          />
        ) : null}
      </div>

      {/* Bottom info bar */}
      <div className="flex-shrink-0 border-t border-zinc-800 px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          {/* Metadata */}
          <div className="flex items-center gap-3 text-xs text-zinc-500 flex-shrink-0 min-w-0">
            <span className="text-zinc-400 font-medium">{result.model}</span>
            <span>{result.width}&times;{result.height}</span>
            <span>{new Date(result.timestamp).toLocaleString()}</span>
          </div>

          {/* Prompt text (truncated) */}
          <div className="flex-1 min-w-0 max-h-12 overflow-y-auto">
            <p className="text-xs text-zinc-400 leading-relaxed truncate">{result.prompt}</p>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={handleSelect}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded transition-colors ${
                isSelected
                  ? 'bg-green-600 text-white'
                  : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
              }`}
              title="Use for Canvas"
            >
              <Check size={12} />
              {isSelected ? 'Selected' : 'Use for Canvas'}
            </button>
            <button
              onClick={handleRegenerate}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-zinc-700 text-zinc-300 hover:bg-zinc-600 rounded transition-colors"
              title="Regenerate"
            >
              <RefreshCw size={12} />
              Regenerate
            </button>
            <button
              onClick={handleSave}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-zinc-700 text-zinc-300 hover:bg-zinc-600 rounded transition-colors"
              title="Save"
            >
              <Download size={12} />
              Save
            </button>
            <button
              onClick={handleDelete}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-zinc-700 text-zinc-300 hover:bg-red-900/80 rounded transition-colors"
              title="Delete"
            >
              <Trash2 size={12} />
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
